/*
 * MFA callback page - exchanges the Ping MFA result for an access token and completes login
 * <p>
 *
 * @author lachezar.parvin
 * @author Gallagher Bassett IT
 * @author Copyright (c) 2020 Gallagher Bassett Services, Inc.
 *
 */

import {
  Component,
  OnDestroy,
  OnInit
} from '@angular/core';
import { Location } from '@angular/common';
import {
  ActivatedRoute,
  Params,
  Router,
  RouterLink
} from '@angular/router';
import { JwtHelperService } from '@auth0/angular-jwt';
import { Constants } from 'app/shared/config/constants';
import { isHandOffApp } from 'app/shared/lib';
import { AuthService } from 'app/shared/services/auth.service';
import { MfaService } from 'app/shared/services/mfa.service';
import { PostLoginRedirectService } from 'app/shared/services/post-login-redirect.service';
import { UiConfigService } from 'app/shared/services/ui-config.service';

export type MfaCallbackStatus = 'validating' | 'error' | 'expired';

@Component ( {
  selector: 'reporter-mfa-callback',
  imports: [ RouterLink ],
  templateUrl: './mfa-callback.component.html',
  styleUrl: './mfa-callback.component.scss',
} )
export class MfaCallbackComponent implements OnInit, OnDestroy {

  status: MfaCallbackStatus = 'validating';
  loginUrl = '/login';
  loginQueryParams: Params | null = null;

  private returnToLoginHandle: ReturnType<typeof setTimeout> | undefined;

  constructor (
    private mfaService: MfaService,
    private route: ActivatedRoute,
    private router: Router,
    private authService: AuthService,
    private postLoginRedirectService: PostLoginRedirectService,
    private uiConfigService: UiConfigService,
    private jwtHelper: JwtHelperService,
    private location: Location,
  ) {
  }

  async ngOnInit (): Promise<void> {
    const urlSegmentKey = this.uiConfigService.urlSegmentKey;
    this.loginUrl = urlSegmentKey ? `/${ urlSegmentKey }/login` : '/login';

    // Must be read before exchangeMfaToken, because setAuthenticated(true) removes it.
    const gbru = this.postLoginRedirectService.parseGbru ( localStorage.getItem ( Constants.localStorageKeys.___gbru ) );
    const pendingSession = this.mfaService.read ();
    this.mfaService.clear ();
    const authRedirectApp = pendingSession
      ? pendingSession.authRedirectApp
      : sessionStorage.getItem ( Constants.authRedirectAppStorageKey );
    if ( isHandOffApp ( authRedirectApp ) ) {
      this.loginQueryParams = { [ Constants.authRedirectAppParam ]: authRedirectApp };
    }

    const tokenParam = this.route.snapshot.queryParams[ Constants.mfaCallbackTokenParam ];
    const ssoToken = typeof tokenParam === 'string' ? tokenParam.trim () : '';
    this.removeQueryFromHistory ();

    if ( !pendingSession ) {
      this.fail ( 'expired' );
      return;
    }

    if ( !ssoToken ) {
      this.fail ( 'error' );
      return;
    }

    let handOffToken: string | undefined;
    try {
      if ( isHandOffApp ( pendingSession.authRedirectApp ) ) {
        handOffToken = await this.authService.exchangeMfaTokenForHandOff ( pendingSession.mfaToken, ssoToken );
      } else {
        await this.authService.exchangeMfaToken ( pendingSession.mfaToken, ssoToken, pendingSession.urlSegmentKey );
      }
    } catch ( e ) {
      const expired = this.isTokenExpired ( pendingSession.mfaToken ) || this.isTokenExpired ( ssoToken );
      this.fail ( expired ? 'expired' : 'error' );
      return;
    }

    const redirected = await this.postLoginRedirectService.redirect ( {
      gbru,
      redirectPath: pendingSession.redirectPath,
      returnUrl: pendingSession.returnUrl,
      authRedirectApp: pendingSession.authRedirectApp,
      credentialsLogin: true,
      ...( handOffToken ? { handOffToken } : {} ),
    } );

    if ( redirected === false ) {
      this.fail ( 'error' );
    }
  }

  ngOnDestroy (): void {
    if ( this.returnToLoginHandle ) {
      clearTimeout ( this.returnToLoginHandle );
    }
  }

  private fail ( status: MfaCallbackStatus ): void {
    this.status = status;
    this.returnToLoginHandle = setTimeout ( () => {
      this.router.navigate ( [ this.loginUrl ], {
        replaceUrl: true,
        ...( this.loginQueryParams ? { queryParams: this.loginQueryParams } : {} ),
      } );
    }, 5000 );
  }

  private removeQueryFromHistory (): void {
    const [ path ] = this.location.path ().split ( '?' );
    this.location.replaceState ( path, '', this.location.getState () );
  }

  private isTokenExpired ( token: string ): boolean {
    try {
      return this.jwtHelper.isTokenExpired ( token );
    } catch ( e ) {
      return false;
    }
  }
}

